import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import { isDocumentUploaded } from "@/components/DocumentInput/useDocumentInput";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { parseErrorMessage } from "@/helpers/Network/errors";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { documentMutations } from "@/queries/documents/mutations";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { fileMutations } from "@/queries/files/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { DocumentFileForm, type DocumentFileFormProps, type DocumentFileFormValues } from "./DocumentFileForm";

export function CreateDocumentFilePage(): React.ReactNode {
  const slug = useSlug();
  const [isCreating, isCreatingHandlers] = useBool(false);
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const { folderId } = useParams<{ folderId: string }>();

  const documentQueries = useDocumentQueries();
  const {
    data: parentFolder,
    isPending: parentFolderIsPending,
    error: parentFolderError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: folderId!,
    }),
    enabled: !!folderId,
    select: (res) => {
      const data = commonAPIDataSelector(res);

      return {
        id: data.id,
        name: data.name,
      };
    },
  });

  const createDocumentMutation = fileMutations.useAddDocument();
  const createFileMutation = documentMutations.useCreateFile(folderId!);

  async function uploadDocumentIfNotUploaded(data: DocumentFileFormValues) {
    const document = data.documents?.[0];
    const link = data.link?.trim();
    if (isDocumentUploaded(document)) {
      return document;
    } else if (document) {
      return await createDocumentMutation.mutateAsync({ file: document.file });
    } else if (link) {
      return await createDocumentMutation.mutateAsync({ link });
    } else {
      throw new Error("Missing document/link");
    }
  }

  async function create(data: DocumentFileFormValues) {
    isCreatingHandlers.setTrue();
    try {
      const upload = await uploadDocumentIfNotUploaded(data);

      await createFileMutation.mutateAsync({
        name: data.name,
        nameTranslations: [],
        parentFolderId: folderId!,
        uploadId: upload.id,
      });

      showFlashToast({
        variant: "success",
        message: t("model.document.file.action.create.notification.success"),
      });

      void navigate(routes.documents.documentsList({ slug, folderId: folderId! }));
    } catch (error) {
      showFlashToast({
        variant: "alert",
        message: t("model.document.file.action.create.notification.error"),
        description: parseErrorMessage(error),
      });

      isCreatingHandlers.setFalse();
    }
  }

  if (parentFolderError) {
    return <ErrorPage error={parentFolderError} />;
  }

  if (parentFolderIsPending) {
    return <FullSizeLoader withPadding />;
  }

  const defaultFormValues: DocumentFileFormValues = {
    link: "",
    name: "",
    documents: [],
  };

  const formProps: DocumentFileFormProps = {
    folderName: parentFolder.name,
    folderId: parentFolder.id,
    defaultFormValues,
    originalLanguageId: undefined,
    onSubmit: create,
    isSubmitting: isCreating || createFileMutation.isPending || createDocumentMutation.isPending,
    isEditMode: false,
  };

  return <DocumentFileForm {...formProps} />;
}
